import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ShippingRouteStatus } from '~/graphql/graphql';
import type {
  DeliveryMethod,
  LogisticsIncoterm,
  MsrIncoTerm,
} from '~/graphql/graphql';

/** Returns a formatter for incoterm labels ("FOB — Free On Board"); em dash when unset. */
export function useIncotermLabel(): (
  incoterm: LogisticsIncoterm | MsrIncoTerm | null | undefined
) => string {
  const { t } = useTranslation('cart');
  return useCallback(
    (incoterm) =>
      incoterm
        ? t(`cart.incoterm.${incoterm}` as const, { defaultValue: incoterm })
        : '—',
    [t]
  );
}

/** Two-leg terms with one inland CY/RAMP/DOOR endpoint and one seaport. */
export const isComboIncoterm = (
  incoterm: LogisticsIncoterm | null | undefined
): boolean =>
  incoterm === 'FCA_COMBO' ||
  incoterm === 'DAP_COMBO' ||
  incoterm === 'DDP_COMBO';

/** Terms whose destination is a free-text address, not a named seaport. */
export const incotermUsesAddress = (
  incoterm: LogisticsIncoterm | null | undefined
): boolean => incoterm === 'FCA' || incoterm === 'DAP' || incoterm === 'DDP';

export const incotermRequiresAddress = (
  incoterm: LogisticsIncoterm | null | undefined
): boolean => incotermUsesAddress(incoterm) || isComboIncoterm(incoterm);

type InternationalShippingFields = {
  deliveryMethod?: DeliveryMethod | null;
  incotermDischarge?: LogisticsIncoterm | null;
  incotermAddressDestination?: string | null;
  portOfLoading?: { id: string } | null;
  portOfDischarge?: { id: string } | null;
  shippingRoute?: { id: string; status: ShippingRouteStatus } | null;
};

export const isInternationalShippingComplete = (
  cart: InternationalShippingFields
): boolean =>
  Boolean(
    cart.deliveryMethod &&
    cart.portOfLoading?.id &&
    cart.portOfDischarge?.id &&
    cart.shippingRoute?.id &&
    cart.shippingRoute.status === ShippingRouteStatus.Active &&
    (!incotermRequiresAddress(cart.incotermDischarge) ||
      cart.incotermAddressDestination?.trim())
  );

export const isInternationalComplete = (
  cart: InternationalShippingFields,
  isInternational: boolean
): boolean => !isInternational || isInternationalShippingComplete(cart);
